import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, Edit3, Key, Loader, Plus, QrCode, RefreshCw, Shield, Smartphone } from 'lucide-react';
import moment from 'moment';
import { QRCode } from 'antd';
import { messageI18n } from '../../../helpers/i18n/toastI18n';
import { buildEnrollmentPayload, currentOrgCode } from '../../../helpers/orgCode';
import { buildEnrollmentSheet } from '../../../helpers/enrollmentPdf';

import { Badge } from '../../ComponnentsUI/common/Badge';
import { Button } from '../../ComponnentsUI/common/Button';
import { Card } from '../../ComponnentsUI/common/Card';
import { Input } from '../../ComponnentsUI/common/Input';
import { Select } from '../../ComponnentsUI/common/Select';
import { Modal } from '../../ComponnentsUI/common/Modal';
import {
  MobileDeviceUpdateInput,
  MobileEnrolledDevice,
  MobileLicense,
  MobileLicenseCreateInput,
  MobileLicenseUpdateInput,
  MobileManifestArtifact,
  MobileManifestArtifactInput,
  MobileManifestRelease,
  MobileManifestReleaseCreateInput,
  MobileManifestReleaseUpdateInput,
  listMobileManifestArtifacts,
  useCreateMobileLicense,
  useCreateMobileManifestRelease,
  useMobileLicensingOverview,
  useUpdateMobileDevice,
  useUpdateMobileLicense,
  useUpdateMobileManifestRelease,
} from '../../../helpers/api/organization/mobile-licensing';

type LicenseFormState = MobileLicenseCreateInput;
type ManifestFormState = MobileManifestReleaseCreateInput;
type DeviceFormState = Omit<MobileDeviceUpdateInput, 'id'>;
type ManifestArtifactFormState = {
  id?: string;
  model_key: string;
  model_version: string;
  artifact_path: string;
  is_required: boolean;
};

const defaultLicenseForm: LicenseFormState = {
  label: '',
  license_type: 'device_seat',
  status: 'active',
  seat_limit: 1,
  expires_at: null,
  notes: '',
};

const defaultManifestForm: ManifestFormState = {
  label: '',
  manifest_version: '',
  status: 'draft',
  artifact_count: 0,
  is_active: false,
  published_at: null,
  notes: '',
};

const MOBILE_PROVISIONING_ROOT = '/mobile-provisioning';

const createEmptyManifestArtifact = (): ManifestArtifactFormState => ({
  model_key: '',
  model_version: '1',
  artifact_path: '',
  is_required: true,
});

const defaultDeviceForm: DeviceFormState = {
  device_label: '',
  app_version: '',
  status: 'active',
  license_id: null,
};

export const MobileLicensingTab: React.FC = () => {
  const { t } = useTranslation('Organization');
  const { data, isLoading, error, refetch } = useMobileLicensingOverview();

  const summary = data?.summary ?? {
    license_count: 0,
    active_license_count: 0,
    total_seats: 0,
    assigned_seats: 0,
    device_count: 0,
    active_device_count: 0,
    active_manifest_version: null,
  };

  const licenses = data?.licenses ?? [];
  const devices = data?.devices ?? [];
  const manifests = data?.manifests ?? [];

  const licenseNameById = useMemo(
    () => new Map(licenses.map((license) => [license.id, license.label])),
    [licenses]
  );

  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<MobileLicense | null>(null);
  const [licenseForm, setLicenseForm] = useState<LicenseFormState>(defaultLicenseForm);

  const [manifestModalOpen, setManifestModalOpen] = useState(false);
  const [editingManifest, setEditingManifest] = useState<MobileManifestRelease | null>(null);
  const [manifestForm, setManifestForm] = useState<ManifestFormState>(defaultManifestForm);
  const [manifestArtifacts, setManifestArtifacts] = useState<ManifestArtifactFormState[]>([
    createEmptyManifestArtifact(),
  ]);
  const [manifestArtifactsLoading, setManifestArtifactsLoading] = useState(false);

  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<MobileEnrolledDevice | null>(null);
  const [deviceForm, setDeviceForm] = useState<DeviceFormState>(defaultDeviceForm);

  const [qrLicense, setQrLicense] = useState<MobileLicense | null>(null);
  const [isBuildingSheet, setIsBuildingSheet] = useState(false);
  // The on-screen QR is sized for the modal; printing it at 62mm would land around 90dpi.
  // A second, hidden render at 1024px is the print source, so the sheet stays scannable.
  const qrPrintRef = useRef<HTMLDivElement | null>(null);
  // Read once per open rather than per render: the host cannot change while the page is
  // loaded, and this keeps the value shown next to the QR identical to the encoded one.
  const qrOrgCode = useMemo(() => currentOrgCode(), []);

  const createLicenseMutation = useCreateMobileLicense(
    () => {
      setLicenseModalOpen(false);
      setEditingLicense(null);
      setLicenseForm(defaultLicenseForm);
    },
    undefined,
    true
  );

  const updateLicenseMutation = useUpdateMobileLicense(
    () => {
      setLicenseModalOpen(false);
      setEditingLicense(null);
      setLicenseForm(defaultLicenseForm);
    },
    undefined,
    true
  );

  const createManifestMutation = useCreateMobileManifestRelease(
    () => {
      setManifestModalOpen(false);
      setEditingManifest(null);
      setManifestForm(defaultManifestForm);
      setManifestArtifacts([createEmptyManifestArtifact()]);
      setManifestArtifactsLoading(false);
    },
    undefined,
    true
  );

  const updateManifestMutation = useUpdateMobileManifestRelease(
    () => {
      setManifestModalOpen(false);
      setEditingManifest(null);
      setManifestForm(defaultManifestForm);
      setManifestArtifacts([createEmptyManifestArtifact()]);
      setManifestArtifactsLoading(false);
    },
    undefined,
    true
  );

  const updateDeviceMutation = useUpdateMobileDevice(
    () => {
      setDeviceModalOpen(false);
      setEditingDevice(null);
      setDeviceForm(defaultDeviceForm);
    },
    undefined,
    true
  );

  useEffect(() => {
    if (editingLicense == null) {
      setLicenseForm(defaultLicenseForm);
      return;
    }

    setLicenseForm({
      label: editingLicense.label,
      license_type: editingLicense.license_type,
      status: editingLicense.status,
      seat_limit: editingLicense.seat_limit,
      expires_at: editingLicense.expires_at ?? null,
      notes: editingLicense.notes ?? '',
    });
  }, [editingLicense]);

  useEffect(() => {
    let cancelled = false;

    if (editingManifest == null) {
      setManifestForm(defaultManifestForm);
      setManifestArtifacts([createEmptyManifestArtifact()]);
      setManifestArtifactsLoading(false);
      return;
    }

    setManifestForm({
      label: editingManifest.label,
      manifest_version: editingManifest.manifest_version,
      status: editingManifest.status,
      artifact_count: editingManifest.artifact_count,
      is_active: editingManifest.is_active,
      published_at: editingManifest.published_at ?? null,
      notes: editingManifest.notes ?? '',
    });
    setManifestArtifactsLoading(true);

    void listMobileManifestArtifacts(editingManifest.id)
      .then((artifacts) => {
        if (cancelled) return;

        const enabledArtifacts = artifacts
          .filter((artifact) => artifact.is_enabled !== false)
          .sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0));

        setManifestArtifacts(
          enabledArtifacts.length > 0
            ? enabledArtifacts.map((artifact) => mapManifestArtifactToForm(artifact))
            : [createEmptyManifestArtifact()]
        );
      })
      .catch(() => {
        if (cancelled) return;
        setManifestArtifacts([createEmptyManifestArtifact()]);
        messageI18n.error('mobile.releaseModelPathsFailed');
      })
      .finally(() => {
        if (cancelled) return;
        setManifestArtifactsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editingManifest]);

  useEffect(() => {
    if (editingDevice == null) {
      setDeviceForm(defaultDeviceForm);
      return;
    }

    setDeviceForm({
      device_label: editingDevice.device_label,
      app_version: editingDevice.app_version ?? '',
      status: editingDevice.status,
      license_id: editingDevice.license_id ?? null,
    });
  }, [editingDevice]);

  const onCreateLicense = () => {
    setEditingLicense(null);
    setLicenseModalOpen(true);
  };

  const onCreateManifest = () => {
    setEditingManifest(null);
    setManifestForm(defaultManifestForm);
    setManifestArtifacts([createEmptyManifestArtifact()]);
    setManifestArtifactsLoading(false);
    setManifestModalOpen(true);
  };

  const onEditLicense = (license: MobileLicense) => {
    setEditingLicense(license);
    setLicenseModalOpen(true);
  };

  const onEditManifest = (manifest: MobileManifestRelease) => {
    setEditingManifest(manifest);
    setManifestModalOpen(true);
  };

  const onManageDevice = (device: MobileEnrolledDevice) => {
    setEditingDevice(device);
    setDeviceModalOpen(true);
  };

  const submitLicense = () => {
    if (!licenseForm.label.trim()) {
      messageI18n.error('mobile.licenseLabelRequired');
      return;
    }

    const payload = {
      ...licenseForm,
      label: licenseForm.label.trim(),
      notes: cleanOptionalString(licenseForm.notes),
      expires_at: cleanOptionalString(licenseForm.expires_at),
    };

    if (editingLicense) {
      const updatePayload: MobileLicenseUpdateInput = {
        id: editingLicense.id,
        ...payload,
      };
      updateLicenseMutation.mutate(updatePayload);
      return;
    }

    createLicenseMutation.mutate(payload);
  };

  const submitManifest = () => {
    if (!manifestForm.label.trim() || !manifestForm.manifest_version.trim()) {
      messageI18n.error('mobile.releaseLabelRequired');
      return;
    }

    const artifacts = normalizeManifestArtifacts(manifestArtifacts);
    if (artifacts.length === 0) {
      messageI18n.error('mobile.addModelPath');
      return;
    }

    if (artifacts.some((artifact) => !artifact.model_key || !artifact.model_version || !artifact.source_uri)) {
      messageI18n.error('mobile.modelPathFieldsRequired');
      return;
    }

    if (artifacts.some((artifact) => !isRelativeManifestArtifactPath(artifact.source_uri))) {
      messageI18n.error('mobile.modelPathRoot', { root: MOBILE_PROVISIONING_ROOT });
      return;
    }

    const payload = {
      ...manifestForm,
      label: manifestForm.label.trim(),
      manifest_version: manifestForm.manifest_version.trim(),
      artifact_count: artifacts.length,
      notes: cleanOptionalString(manifestForm.notes),
      published_at: cleanOptionalString(manifestForm.published_at),
      artifacts,
    };

    if (editingManifest) {
      const updatePayload: MobileManifestReleaseUpdateInput = {
        id: editingManifest.id,
        ...payload,
      };
      updateManifestMutation.mutate(updatePayload);
      return;
    }

    createManifestMutation.mutate(payload);
  };

  const submitDevice = () => {
    if (!editingDevice) {
      return;
    }

    if (!deviceForm.device_label?.trim()) {
      messageI18n.error('mobile.deviceLabelRequired');
      return;
    }

    updateDeviceMutation.mutate({
      id: editingDevice.id,
      device_label: deviceForm.device_label.trim(),
      app_version: cleanOptionalString(deviceForm.app_version),
      status: deviceForm.status,
      license_id: deviceForm.license_id ?? null,
    });
  };

  const copyLicenseKey = async (licenseKey: string) => {
    try {
      await navigator.clipboard.writeText(licenseKey);
      messageI18n.success('mobile.licenseKeyCopied');
    } catch {
      messageI18n.error('mobile.licenseKeyCopyFailed');
    }
  };

  const downloadEnrollmentSheet = async () => {
    const canvas = qrPrintRef.current?.querySelector('canvas');
    if (!canvas || !qrLicense) {
      messageI18n.error('mobile.qrDownloadFailed');
      return;
    }

    setIsBuildingSheet(true);
    try {
      const pdf = await buildEnrollmentSheet({
        orgCode: qrOrgCode,
        licenseKey: qrLicense.license_key,
        licenseLabel: qrLicense.label,
        qrCanvas: canvas,
        strings: {
          title: t('mobile.sheet.title', 'Register your DeepEcho device'),
          intro: t(
            'mobile.sheet.intro',
            'This sheet activates one mobile device. Install the DeepEcho app, open it, and you will be asked to register the device before signing in.'
          ),
          steps: [
            t('mobile.sheet.step1', 'Open the DeepEcho app on the tablet or phone.'),
            t(
              'mobile.sheet.step2',
              'On the "Register this device" screen, scan the code above.'
            ),
            t(
              'mobile.sheet.step3',
              'Sign in with the email and password issued to you. If it is your first sign-in, you will be asked to choose a new password.'
            ),
          ],
          orgCodeLabel: t('mobile.qrModal.orgCode', 'Organization code'),
          licenseKeyLabel: t('mobile.activationKey'),
          manualHint: t(
            'mobile.sheet.manualHint',
            'If the camera cannot read the code, type these two values into the registration screen exactly as shown.'
          ),
          generatedOn: t('mobile.sheet.generatedOn', {
            date: moment().format('MMM D, YYYY'),
            defaultValue: `Generated ${moment().format('MMM D, YYYY')}`,
          }),
        },
      });

      pdf.save(`deepecho-enrollment-${qrOrgCode}-${qrLicense.license_key}.pdf`);
    } catch {
      messageI18n.error('mobile.qrDownloadFailed');
    } finally {
      setIsBuildingSheet(false);
    }
  };

  const isLicenseSubmitting =
    createLicenseMutation.isPending || updateLicenseMutation.isPending;
  const isManifestSubmitting =
    createManifestMutation.isPending || updateManifestMutation.isPending;
  const isDeviceSubmitting = updateDeviceMutation.isPending;

  const mobileStatusLabel = (status?: string) => {
    const key = (status || '').toLowerCase();
    if (!key) {
      return status || '';
    }
    return t(`mobile.status.${key}`, { defaultValue: status || key });
  };

  const manifestBadgeLabel = (manifest: MobileManifestRelease) => {
    if (manifest.is_active) {
      return t('mobile.manifestBadge.current');
    }
    if ((manifest.status || '').toLowerCase() === 'active') {
      return t('mobile.manifestBadge.published');
    }
    return mobileStatusLabel(manifest.status);
  };

  const licenseStatusOptions = [
    'active',
    'draft',
    'revoked',
    'expired',
  ].map((value) => ({ value, label: mobileStatusLabel(value) }));

  const manifestStatusOptions = ['draft', 'active', 'archived'].map((value) => ({
    value,
    label: mobileStatusLabel(value),
  }));

  const deviceStatusOptions = ['active', 'blocked', 'revoked', 'pending'].map(
    (value) => ({ value, label: mobileStatusLabel(value) })
  );

  return (
    <>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MobileSummaryTile
            icon={<Key size={18} className="text-blue-600 dark:text-blue-400" />}
            label={t('mobile.licenses')}
            value={String(summary.license_count)}
            hint={t('mobile.summaryActive', { count: summary.active_license_count })}
          />
          <MobileSummaryTile
            icon={<Shield size={18} className="text-emerald-600 dark:text-emerald-400" />}
            label={t('mobile.seats')}
            value={`${summary.assigned_seats}/${summary.total_seats}`}
            hint={t('mobile.summarySeatsHint')}
          />
          <MobileSummaryTile
            icon={<Smartphone size={18} className="text-violet-600 dark:text-violet-400" />}
            label={t('mobile.devices')}
            value={String(summary.device_count)}
            hint={t('mobile.summaryActive', { count: summary.active_device_count })}
          />
          <MobileSummaryTile
            icon={<ClipboardCheck size={18} className="text-amber-600 dark:text-amber-400" />}
            label={t('mobile.activeRelease')}
            value={summary.active_manifest_version || t('mobile.none')}
            hint={t('mobile.summaryManifestHint')}
          />
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('mobile.title')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('mobile.description')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={onCreateManifest}>
              <Plus size={14} className="mr-2" />
              {t('mobile.newRelease')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              <RefreshCw size={14} className="mr-2" />
              {t('mobile.refresh')}
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
            {t('mobile.loadError')}
          </div>
        )}

        {isLoading ? (
          <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <Loader className="animate-spin text-blue-600" size={24} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <Card title={t('mobile.licensesCard')} className="min-h-[280px]">
              {licenses.length === 0 ? (
                <MobileEmptyState
                  icon={<Key size={20} />}
                  text={t('mobile.noLicenses')}
                  hint={t(
                    'mobile.noLicensesHint',
                    'Create a license to generate an activation key for mobile devices.'
                  )}
                  actionLabel={t('mobile.newLicense')}
                  onAction={onCreateLicense}
                />
              ) : (
                <div className="space-y-3">
                  {licenses.map((license) => (
                    <div
                      key={license.id}
                      className="rounded-xl border border-slate-200 p-4 dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {license.label}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {license.license_type.replaceAll('_', ' ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            text={mobileStatusLabel(license.status)}
                            variant={statusToBadgeVariant(license.status)}
                          />
                        </div>
                      </div>
                      <div className="mt-3 rounded-lg bg-slate-50 p-3 dark:bg-gray-900/40">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              {t('mobile.activationKey')}
                            </div>
                            <div className="mt-1 break-all font-mono text-sm text-slate-900 dark:text-white">
                              {license.license_key}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyLicenseKey(license.license_key)}
                            >
                              {t('mobile.copyKey')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setQrLicense(license)}
                              title={t('mobile.enrollmentQr', 'Enrollment QR code')}
                            >
                              <QrCode size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                      {license.expires_at && (
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {t('mobile.expires', {
                            date: moment(license.expires_at).format('MMM D, YYYY'),
                          })}
                        </div>
                      )}
                      {license.notes && (
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          {license.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title={t('mobile.devicesCard')} className="min-h-[280px]">
              {devices.length === 0 ? (
                <MobileEmptyState
                  icon={<Smartphone size={20} />}
                  text={t('mobile.noDevices')}
                  hint={t(
                    'mobile.noDevicesHint',
                    'Devices appear here automatically once they activate with a license key.'
                  )}
                />
              ) : (
                <div className="space-y-3">
                  {devices.map((device) => (
                    <div
                      key={device.id}
                      className="rounded-xl border border-slate-200 p-4 dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {device.device_label}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {device.app_version || t('mobile.appVersionUnknown')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            text={mobileStatusLabel(device.status)}
                            variant={statusToBadgeVariant(device.status)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onManageDevice(device)}
                          >
                            <Edit3 size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                        {t('mobile.licenseLabel', {
                          name: device.license_id
                            ? licenseNameById.get(device.license_id) || t('mobile.unknown')
                            : t('mobile.unassigned'),
                        })}
                      </div>
                      <div className="mt-2 break-all text-xs text-slate-500 dark:text-slate-400">
                        {t('mobile.fingerprintLabel')}: {device.device_fingerprint}
                      </div>
                      {device.last_seen_at && (
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          {t('mobile.lastSeen', {
                            time: moment(device.last_seen_at).fromNow(),
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title={t('mobile.manifestsCard')} className="min-h-[280px]">
              {manifests.length === 0 ? (
                <MobileEmptyState
                  icon={<ClipboardCheck size={20} />}
                  text={t('mobile.noManifests')}
                  hint={t(
                    'mobile.noManifestsHint',
                    'Publish a release to control which model versions mobile devices download.'
                  )}
                  actionLabel={t('mobile.newRelease')}
                  onAction={onCreateManifest}
                />
              ) : (
                <div className="space-y-3">
                  {manifests.map((manifest) => (
                    <div
                      key={manifest.id}
                      className="rounded-xl border border-slate-200 p-4 dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {manifest.label}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {manifest.manifest_version}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            text={manifestBadgeLabel(manifest)}
                            variant={manifest.is_active ? 'success' : statusToBadgeVariant(manifest.status)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditManifest(manifest)}
                          >
                            <Edit3 size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                        {t('mobile.artifacts', { count: manifest.artifact_count })}
                      </div>
                      {manifest.published_at && (
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {t('mobile.published', {
                            date: moment(manifest.published_at).format('MMM D, YYYY'),
                          })}
                        </div>
                      )}
                      {manifest.notes && (
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          {manifest.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      <Modal
        isOpen={licenseModalOpen}
        onClose={() => {
          if (isLicenseSubmitting) return;
          setLicenseModalOpen(false);
          setEditingLicense(null);
        }}
        size="lg"
      >
        <Modal.Header
          title={
            editingLicense
              ? t('mobile.licenseModal.editTitle')
              : t('mobile.licenseModal.newTitle')
          }
          onClose={() => {
            if (isLicenseSubmitting) return;
            setLicenseModalOpen(false);
            setEditingLicense(null);
          }}
          description={t('mobile.licenseModal.description')}
        />
        <Modal.Body className="space-y-4">
          <MobileFormField label={t('mobile.licenseModal.label')}>
            <Input
              value={licenseForm.label}
              onChange={(event) =>
                setLicenseForm((current) => ({ ...current, label: event.target.value }))
              }
              placeholder={t('mobile.licenseModal.labelPlaceholder')}
            />
          </MobileFormField>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <MobileFormField label={t('mobile.licenseModal.type')}>
              <MobileSelect
                value={licenseForm.license_type || 'device_seat'}
                onChange={(value) =>
                  setLicenseForm((current) => ({ ...current, license_type: value }))
                }
                options={[
                  { value: 'device_seat', label: t('mobile.licenseTypes.deviceSeat') },
                  { value: 'exam_pack', label: t('mobile.licenseTypes.examPack') },
                  { value: 'site_bundle', label: t('mobile.licenseTypes.siteBundle') },
                ]}
              />
            </MobileFormField>
            <MobileFormField label={t('mobile.licenseModal.status')}>
              <MobileSelect
                value={licenseForm.status || 'active'}
                onChange={(value) =>
                  setLicenseForm((current) => ({ ...current, status: value }))
                }
                options={licenseStatusOptions}
              />
            </MobileFormField>
            <MobileFormField label={t('mobile.licenseModal.seatLimit')}>
              <Input
                type="number"
                min={0}
                value={String(licenseForm.seat_limit)}
                onChange={(event) =>
                  setLicenseForm((current) => ({
                    ...current,
                    seat_limit: normalizeNumber(event.target.value),
                  }))
                }
              />
            </MobileFormField>
          </div>

          <MobileFormField label={t('mobile.licenseModal.expiresAt')}>
            <Input
              type="datetime-local"
              value={toDateTimeLocalValue(licenseForm.expires_at)}
              onChange={(event) =>
                setLicenseForm((current) => ({
                  ...current,
                  expires_at: event.target.value ? fromDateTimeLocalValue(event.target.value) : null,
                }))
              }
            />
          </MobileFormField>

          <MobileFormField label={t('mobile.licenseModal.notes')}>
            <MobileTextArea
              value={licenseForm.notes || ''}
              onChange={(event) =>
                setLicenseForm((current) => ({ ...current, notes: event.target.value }))
              }
              placeholder={t('mobile.licenseModal.notesPlaceholder')}
            />
          </MobileFormField>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="ghost"
            onClick={() => {
              if (isLicenseSubmitting) return;
              setLicenseModalOpen(false);
              setEditingLicense(null);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button onClick={submitLicense} disabled={isLicenseSubmitting}>
            {isLicenseSubmitting
              ? t('mobile.licenseModal.saving')
              : editingLicense
                ? t('mobile.licenseModal.saveChanges')
                : t('mobile.licenseModal.create')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        isOpen={manifestModalOpen}
        onClose={() => {
          if (isManifestSubmitting) return;
          setManifestModalOpen(false);
          setEditingManifest(null);
          setManifestForm(defaultManifestForm);
          setManifestArtifacts([createEmptyManifestArtifact()]);
          setManifestArtifactsLoading(false);
        }}
        size="lg"
      >
        <Modal.Header
          title={
            editingManifest
              ? t('mobile.manifestModal.editTitle')
              : t('mobile.manifestModal.newTitle')
          }
          onClose={() => {
            if (isManifestSubmitting) return;
            setManifestModalOpen(false);
            setEditingManifest(null);
            setManifestForm(defaultManifestForm);
            setManifestArtifacts([createEmptyManifestArtifact()]);
            setManifestArtifactsLoading(false);
          }}
          description={t('mobile.manifestModal.description')}
        />
        <Modal.Body className="space-y-4">
          <MobileFormField label={t('mobile.manifestModal.label')}>
            <Input
              value={manifestForm.label}
              onChange={(event) =>
                setManifestForm((current) => ({ ...current, label: event.target.value }))
              }
              placeholder={t('mobile.manifestModal.labelPlaceholder')}
            />
          </MobileFormField>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <MobileFormField label={t('mobile.manifestModal.manifestVersion')}>
              <Input
                value={manifestForm.manifest_version}
                onChange={(event) =>
                  setManifestForm((current) => ({
                    ...current,
                    manifest_version: event.target.value,
                  }))
                }
                placeholder={t('mobile.manifestModal.versionPlaceholder')}
              />
            </MobileFormField>
            <MobileFormField label={t('mobile.licenseModal.status')}>
              <MobileSelect
                value={manifestForm.status || 'draft'}
                onChange={(value) =>
                  setManifestForm((current) => ({ ...current, status: value }))
                }
                options={manifestStatusOptions}
              />
            </MobileFormField>
          </div>

          <div className="rounded-xl border border-slate-200 px-4 py-3 dark:border-gray-700">
            <label className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={manifestForm.is_active || false}
                onChange={(event) =>
                  setManifestForm((current) => ({
                    ...current,
                    is_active: event.target.checked,
                    status: event.target.checked ? 'active' : current.status === 'active' ? 'draft' : current.status,
                  }))
                }
              />
              {t('mobile.manifestModal.markActive')}
            </label>
          </div>

          <div className="rounded-xl border border-slate-200 p-4 dark:border-gray-700">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-sm font-medium text-slate-900 dark:text-white">
                  {t('mobile.manifestModal.modelArtifacts')}
                </div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {t('mobile.manifestModal.rootFolder', { root: MOBILE_PROVISIONING_ROOT })}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {t('mobile.manifestModal.pathHint')}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {t('mobile.manifestModal.pathExample')}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setManifestArtifacts((current) => [...current, createEmptyManifestArtifact()])
                }
                disabled={isManifestSubmitting || manifestArtifactsLoading}
              >
                <Plus size={14} className="mr-2" />
                {t('mobile.manifestModal.addModel')}
              </Button>
            </div>

            <div className="mt-4 space-y-4">
              {manifestArtifactsLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Loader className="animate-spin" size={14} />
                  {t('mobile.manifestModal.loadingModels')}
                </div>
              ) : (
                manifestArtifacts.map((artifact, index) => (
                  <div
                    key={artifact.id || `artifact-${index}`}
                    className="rounded-xl border border-slate-200 p-4 dark:border-gray-700"
                  >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <MobileFormField label={t('mobile.manifestModal.modelKey')}>
                        <Input
                          value={artifact.model_key}
                          onChange={(event) =>
                            setManifestArtifacts((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, model_key: event.target.value }
                                  : item
                              )
                            )
                          }
                          placeholder={t('mobile.manifestModal.modelKeyPlaceholder')}
                        />
                      </MobileFormField>

                      <MobileFormField label={t('mobile.manifestModal.modelVersion')}>
                        <Input
                          value={artifact.model_version}
                          onChange={(event) =>
                            setManifestArtifacts((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, model_version: event.target.value }
                                  : item
                              )
                            )
                          }
                          placeholder="1"
                        />
                      </MobileFormField>
                    </div>

                    <MobileFormField label={t('mobile.manifestModal.artifactPath')}>
                      <Input
                        value={artifact.artifact_path}
                        onChange={(event) =>
                          setManifestArtifacts((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, artifact_path: event.target.value }
                                : item
                            )
                          )
                        }
                        placeholder={t('mobile.manifestModal.artifactPathPlaceholder')}
                      />
                    </MobileFormField>

                    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <label className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={artifact.is_required}
                          onChange={(event) =>
                            setManifestArtifacts((current) =>
                              current.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, is_required: event.target.checked }
                                  : item
                              )
                            )
                          }
                        />
                        {t('mobile.manifestModal.requiredProvisioning')}
                      </label>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setManifestArtifacts((current) =>
                            current.filter((_, itemIndex) => itemIndex !== index)
                          )
                        }
                        disabled={isManifestSubmitting || manifestArtifactsLoading}
                      >
                        {t('mobile.manifestModal.remove')}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <MobileFormField label={t('mobile.manifestModal.publishedAt')}>
            <Input
              type="datetime-local"
              value={toDateTimeLocalValue(manifestForm.published_at)}
              onChange={(event) =>
                setManifestForm((current) => ({
                  ...current,
                  published_at: event.target.value ? fromDateTimeLocalValue(event.target.value) : null,
                }))
              }
            />
          </MobileFormField>

          <MobileFormField label={t('mobile.licenseModal.notes')}>
            <MobileTextArea
              value={manifestForm.notes || ''}
              onChange={(event) =>
                setManifestForm((current) => ({ ...current, notes: event.target.value }))
              }
              placeholder={t('mobile.manifestModal.notesPlaceholder')}
            />
          </MobileFormField>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="ghost"
            onClick={() => {
              if (isManifestSubmitting) return;
              setManifestModalOpen(false);
              setEditingManifest(null);
              setManifestForm(defaultManifestForm);
              setManifestArtifacts([createEmptyManifestArtifact()]);
              setManifestArtifactsLoading(false);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button onClick={submitManifest} disabled={isManifestSubmitting}>
            {isManifestSubmitting
              ? t('mobile.licenseModal.saving')
              : editingManifest
                ? t('mobile.licenseModal.saveChanges')
                : t('mobile.manifestModal.createRelease')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        isOpen={deviceModalOpen}
        onClose={() => {
          if (isDeviceSubmitting) return;
          setDeviceModalOpen(false);
          setEditingDevice(null);
        }}
        size="lg"
      >
        <Modal.Header
          title={t('mobile.deviceModal.title')}
          onClose={() => {
            if (isDeviceSubmitting) return;
            setDeviceModalOpen(false);
            setEditingDevice(null);
          }}
          description={t('mobile.deviceModal.description')}
        />
        <Modal.Body className="space-y-4">
          <MobileFormField label={t('mobile.deviceModal.deviceLabel')}>
            <Input
              value={deviceForm.device_label || ''}
              onChange={(event) =>
                setDeviceForm((current) => ({ ...current, device_label: event.target.value }))
              }
            />
          </MobileFormField>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <MobileFormField label={t('mobile.licenseModal.status')}>
              <MobileSelect
                value={deviceForm.status || 'active'}
                onChange={(value) =>
                  setDeviceForm((current) => ({ ...current, status: value }))
                }
                options={deviceStatusOptions}
              />
            </MobileFormField>

            <MobileFormField label={t('mobile.deviceModal.assignedLicense')}>
              <MobileSelect
                value={deviceForm.license_id || ''}
                onChange={(value) =>
                  setDeviceForm((current) => ({
                    ...current,
                    license_id: value || null,
                  }))
                }
                options={[
                  { value: '', label: t('mobile.status.unassigned') },
                  ...licenses.map((license) => ({
                    value: license.id,
                    label: `${license.label} (${license.assigned_seats}/${license.seat_limit})`,
                  })),
                ]}
              />
            </MobileFormField>
          </div>

          <MobileFormField label={t('mobile.deviceModal.appVersion')}>
            <Input
              value={deviceForm.app_version || ''}
              onChange={(event) =>
                setDeviceForm((current) => ({ ...current, app_version: event.target.value }))
              }
              placeholder={t('mobile.deviceModal.appVersionPlaceholder')}
            />
          </MobileFormField>

          {editingDevice && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-400">
              <div className="font-medium text-slate-700 dark:text-slate-300">
                {t('mobile.fingerprintLabel')}
              </div>
              <div className="mt-1 break-all">{editingDevice.device_fingerprint}</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="ghost"
            onClick={() => {
              if (isDeviceSubmitting) return;
              setDeviceModalOpen(false);
              setEditingDevice(null);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button onClick={submitDevice} disabled={isDeviceSubmitting}>
            {isDeviceSubmitting
              ? t('mobile.licenseModal.saving')
              : t('mobile.licenseModal.saveChanges')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal isOpen={qrLicense !== null} onClose={() => setQrLicense(null)}>
        <Modal.Header
          title={t('mobile.qrModal.title', 'Enroll a mobile device')}
          onClose={() => setQrLicense(null)}
        />
        <Modal.Body className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t(
              'mobile.qrModal.description',
              'Scan this from the app’s registration screen, or enter the two values below by hand.'
            )}
          </p>

          <div className="flex justify-center">
            {qrLicense && (
              <QRCode
                value={buildEnrollmentPayload(qrOrgCode, qrLicense.license_key)}
                size={220}
                // Solid white regardless of theme: a dark-mode QR on a dark page is
                // unreadable to a camera, and the sheet is printed as often as scanned.
                bgColor="#ffffff"
                color="#000000"
                errorLevel="M"
              />
            )}
          </div>

          {/*
            Print source, pushed off-screen rather than display:none — a hidden subtree can
            be skipped by layout, and antd has to actually paint the canvas for us to read
            pixels off it.

            Inline styles on purpose: Tailwind only emits classes it finds when scanning
            source, so relying on an arbitrary offset here risks shipping an unstyled div
            that renders a 1024px QR straight into the modal.
          */}
          <div
            ref={qrPrintRef}
            aria-hidden
            style={{
              position: 'absolute',
              left: '-10000px',
              top: 0,
              width: 1024,
              height: 1024,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {qrLicense && (
              <QRCode
                value={buildEnrollmentPayload(qrOrgCode, qrLicense.license_key)}
                size={1024}
                bgColor="#ffffff"
                color="#000000"
                errorLevel="M"
              />
            )}
          </div>

          <div className="space-y-2 rounded-lg bg-slate-50 p-3 dark:bg-gray-900/40">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {t('mobile.qrModal.orgCode', 'Organization code')}
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900 dark:text-white">
                {qrOrgCode}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {t('mobile.activationKey')}
              </div>
              <div className="mt-1 break-all font-mono text-sm text-slate-900 dark:text-white">
                {qrLicense?.license_key}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setQrLicense(null)}>
            {t('Cancel')}
          </Button>
          <Button onClick={downloadEnrollmentSheet} disabled={isBuildingSheet}>
            {isBuildingSheet
              ? t('mobile.qrModal.preparing', 'Preparing…')
              : t('mobile.qrModal.download', 'Download PDF')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const MobileSummaryTile = ({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center justify-between">
      <div className="rounded-xl bg-slate-100 p-2 dark:bg-gray-700">{icon}</div>
      <span className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {label}
      </span>
    </div>
    <div className="mt-5 text-2xl font-semibold text-slate-900 dark:text-white">
      {value}
    </div>
    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</div>
  </div>
);

const MobileEmptyState = ({
  icon,
  text,
  hint,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  text: string;
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
}) => (
  <div className="flex min-h-[180px] flex-col items-center justify-center gap-1 text-center">
    <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-gray-700 dark:text-gray-500">
      {icon}
    </div>
    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{text}</p>
    {hint && <p className="max-w-[260px] text-xs text-slate-400 dark:text-slate-500">{hint}</p>}
    {actionLabel && onAction && (
      <Button variant="outline" size="sm" className="mt-3" onClick={onAction}>
        <Plus size={14} className="mr-1.5" />
        {actionLabel}
      </Button>
    )}
  </div>
);

const mapManifestArtifactToForm = (
  artifact: MobileManifestArtifact
): ManifestArtifactFormState => ({
  id: artifact.id,
  model_key: artifact.model_key,
  model_version: artifact.model_version,
  artifact_path: normalizeManifestArtifactPath(artifact.source_uri),
  is_required: artifact.is_required !== false,
});

const normalizeManifestArtifacts = (
  artifacts: ManifestArtifactFormState[]
): MobileManifestArtifactInput[] =>
  artifacts
    .map((artifact, index) => ({
      id: artifact.id,
      model_key: artifact.model_key.trim(),
      model_version: artifact.model_version.trim(),
      source_uri: normalizeManifestArtifactPath(artifact.artifact_path),
      pack_format: 'onnx',
      sort_order: index,
      is_required: artifact.is_required,
      is_enabled: true,
    }))
    .filter(
      (artifact) =>
        artifact.model_key ||
        artifact.model_version ||
        artifact.source_uri
    );

const normalizeManifestArtifactPath = (value?: string | null) => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }

  const normalizedSlashes = trimmed.replace(/\\/g, '/');
  const mobileRootMatch = normalizedSlashes.match(/\/mobile-provisioning\/(.+)$/);
  if (mobileRootMatch?.[1]) {
    return `/${mobileRootMatch[1].replace(/^\/+/, '')}`;
  }

  const withoutLeadingSlashes = normalizedSlashes.replace(/^\/+/, '');
  if (withoutLeadingSlashes.startsWith('mobile-provisioning/')) {
    return `/${withoutLeadingSlashes.slice('mobile-provisioning/'.length)}`;
  }

  if (
    normalizedSlashes.startsWith('gs://') ||
    normalizedSlashes.startsWith('http://') ||
    normalizedSlashes.startsWith('https://')
  ) {
    return normalizedSlashes;
  }

  return `/${withoutLeadingSlashes}`;
};

const isRelativeManifestArtifactPath = (value?: string | null) => {
  const candidate = (value || '').trim();
  return candidate.startsWith('/') && !candidate.includes('://');
};

const MobileFormField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <div className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</div>
    {children}
  </div>
);

const MobileSelect = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) => (
  <Select
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="flex h-8 w-full rounded-lg border border-slate-300 bg-transparent px-3 py-2 text-sm text-title-page-color-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
    options={options}
  />
);

const MobileTextArea = (
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>
) => (
  <textarea
    {...props}
    className="min-h-[96px] w-full rounded-lg border border-slate-300 bg-transparent px-3 py-2 text-sm text-title-page-color-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
  />
);

const statusToBadgeVariant = (status?: string) => {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'success' as const;
    case 'draft':
    case 'pending':
      return 'warning' as const;
    case 'revoked':
    case 'blocked':
    case 'expired':
    case 'archived':
      return 'danger' as const;
    default:
      return 'neutral' as const;
  }
};

function normalizeNumber(value: string) {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }

  return parsed;
}

function cleanOptionalString(value?: string | null) {
  if (value == null) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toDateTimeLocalValue(value?: string | null) {
  if (!value) {
    return '';
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return '';
  }

  return parsed.format('YYYY-MM-DDTHH:mm');
}

function fromDateTimeLocalValue(value: string) {
  const parsed = moment(value);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.toISOString();
}
